// @ts-nocheck

import { find, includes, omit } from 'lodash';

import { type MatchState } from '../states/entities/matches';
import { type TournamentState } from '../states/entities/tournaments';
import { type SearchResultsType } from '../types/search';
import { type Selector } from '../types/selectors';
import { createResultEqualSelector } from '../utils/reselect';

import { selectEntitiesBets } from './entities/bets/entitiesBetsPrimary.selector';
import { selectEntitiesCategories } from './entities/categories/entitiesCategoriesPrimary.selector';
import { selectEntitiesMatches } from './entities/matches/entitiesMatchesPrimary.selector';
import { selectEntitiesOutcomes } from './entities/outcomes/entitiesOutcomesPrimary.selector';
import { selectEntitiesSports } from './entities/sports/entitiesSportsPrimary.selector';
import { selectEntitiesTournaments } from './entities/tournaments/entitiesTournamentsPrimary.selector';
import {
  selectSearchBets,
  selectSearchMatchIds,
  selectSearchOutcomes,
  selectSearchTournaments,
} from './search/searchPrimary.selector';

const emptyArr = [];

const selectSearchMatchResults: Selector<Array<MatchState>> = createResultEqualSelector(
  [selectEntitiesMatches, selectSearchMatchIds],
  (matches, matchIds) => {
    if (matchIds && matchIds.length) {
      return matchIds.reduce((results, id) => {
        if (matches[id]) {
          results.push(omit(matches[id], ['tvChannels', 'mainBetId']));
        }
        return results;
      }, []);
    }
    return emptyArr;
  },
);

const getCategoryId = (categories, tournamentId) => {
  const finalCategory = find(categories, (category) =>
    category ? includes(category.tournaments, tournamentId) : false,
  );
  return finalCategory ? finalCategory.categoryId : null;
};

const getSportId = (sports, categoryId) => {
  const finalSport = find(sports, (sport) => (sport ? includes(sport.categories, categoryId) : false));
  return finalSport ? finalSport.sportId : null;
};

type SearchTournamentResultsType = Array<
  TournamentState & {
    categoryId: number;
    categoryName: string;
    categoryFlag: string;
    sportId: number;
  }
>;

const selectSearchTournamentResults: Selector<SearchTournamentResultsType> = createResultEqualSelector(
  [selectEntitiesTournaments, selectSearchTournaments, selectEntitiesCategories, selectEntitiesSports],
  (tournaments, tournamentIds, categories, sports) => {
    const filteredTournamentsIds = tournamentIds.filter((id) => tournaments[id]);
    if (filteredTournamentsIds && filteredTournamentsIds.length) {
      return filteredTournamentsIds.reduce((results, id) => {
        const categoryId = getCategoryId(categories, id);
        const sportId = getSportId(sports, categoryId);
        if (sportId && categoryId) {
          const { categoryName, flag } = categories[categoryId];

          results.push({
            ...tournaments[id],
            categoryId,
            categoryName,
            categoryFlag: flag,
            sportId,
          });
        }
        return results;
      }, []);
    }
    return emptyArr;
  },
);

type SearchBetResultsType = Array<{
  matchId: number;
  betId: number;
  betTitle: string;
  sportId: number;
  matchTitle: string;
  matchStart: number;
  betCategoryId?: number;
}>;

const selectSearchBetResults: Selector<SearchBetResultsType> = createResultEqualSelector(
  [selectSearchBets, selectEntitiesSports, selectEntitiesMatches, selectEntitiesBets],
  (searchBets, stateSports, stateMatches, stateBets) => {
    if (searchBets?.length) {
      return searchBets.reduce((result, bet) => {
        if (bet) {
          const { matchId, betId } = bet;
          const { sportId, matchStart, title: matchTitle } = matchId ? stateMatches[matchId] ?? {} : {};
          const { betTitle, betTypeCategoryId } = betId ? stateBets[betId] ?? {} : {};

          if (betTitle) {
            return [
              ...result,
              {
                ...bet,
                betTitle,
                sportId,
                matchTitle,
                matchStart,
                betCategoryId: betTypeCategoryId,
              },
            ];
          }
        }
        return result;
      }, []);
    }
    return emptyArr;
  },
);

type SearchOutcomeResultsType = Array<{
  matchId: number;
  betId: number;
  outcomeId: number;
  betTitle: string;
  outcomeTitle: string;
  sportId: number;
  matchTitle: string;
  matchStart: number;
  betCategoryId?: number;
  playerId?: number;
}>;

const selectSearchOutcomeResults: Selector<SearchOutcomeResultsType> = createResultEqualSelector(
  [selectSearchOutcomes, selectEntitiesSports, selectEntitiesMatches, selectEntitiesBets, selectEntitiesOutcomes],
  (searchOutcomes, stateSports, stateMatches, stateBets, stateOutcomes) => {
    if (searchOutcomes?.length) {
      return searchOutcomes.reduce((result, outcome) => {
        if (outcome) {
          const { matchId, betId, outcomeId } = outcome;
          const { sportId, matchStart, title: matchTitle } = stateMatches[matchId] ?? {};
          const { betTitle, betTypeCategoryId: betCategoryId } = betId ? stateBets[betId] ?? {} : {};
          const { label: outcomeTitle, playerId } = outcomeId ? stateOutcomes[outcomeId] ?? {} : {};

          if (outcomeTitle) {
            return [
              ...result,
              {
                ...outcome,
                betTitle,
                outcomeTitle,
                sportId,
                matchTitle,
                matchStart,
                betCategoryId,
                playerId,
              },
            ];
          }
        }
        return result;
      }, []);
    }
    return [];
  },
);

export const selectSearchResults: Selector<SearchResultsType> = createResultEqualSelector(
  [selectSearchMatchResults, selectSearchTournamentResults, selectSearchBetResults, selectSearchOutcomeResults],
  (matches, tournaments, bets, outcomes) => ({
    matches,
    tournaments,
    bets,
    outcomes,
  }),
);
